import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import type { PortalContact, PortalLayout, PortalLogoSize } from "../../portal/api";
import { PREVIEW_READY, PREVIEW_THEME, type PreviewDocuments, type PreviewMessage, type PreviewScreen } from "../../portal/preview";

/** The theme a frame paints. */
export interface FrameTheme {
  ground: string;
  ink: string;
  accent: string;
  logoUrl: string | null;
  copy?: Record<string, string>;
  /** Page colour; absent or "" is the ground colour. */
  backdrop?: string;
  backgroundUrl?: string | null;
  layout?: PortalLayout;
  cardOpacity?: number;
  logoSize?: PortalLogoSize;
  /** The operator's public contact, so the footer shows what guests see. */
  contact?: PortalContact | null;
  /** The operator's saved documents, so Legal & Terms shows its own text; absent or null shows sample text. */
  documents?: PreviewDocuments | null;
}

const TITLE = "Your Wi-Fi";
const SUBTITLE = "Sign in with your registered mobile number to get online.";

/** The frame sizes the console offers. Desktop is a real desktop width, scaled down to fit. */
export const FRAME_SIZES = {
  phone: { width: 390, height: 780 },
  desktop: { width: 1280, height: 800 },
} as const;

/**
 * The real portal page in a same-origin frame, painted with `theme` and showing
 * `screen` (src/portal/preview.ts). Re-posts on every change, so an editor's
 * keystroke shows on the page at once. Nothing is saved or published by it.
 *
 * 🔑 THE FRAME IS LAID OUT AT ITS REAL WIDTH AND SCALED TO FIT. The portal's
 * desktop layout starts at 900 px, so a desktop preview squeezed into a narrower
 * panel would silently show the phone layout instead -- and a wide or
 * full-screen theme could never be judged.
 */
export function PortalFrame({ theme, screen, width, height }: { theme: FrameTheme; screen: PreviewScreen; width: number; height: number }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState<number | null>(null);
  const latest = useRef({ theme, screen });
  latest.current = { theme, screen };

  const post = () => {
    const win = frame.current?.contentWindow;
    if (!win) return;
    const { theme: t, screen: s } = latest.current;
    const message: PreviewMessage = {
      type: PREVIEW_THEME, title: TITLE, subtitle: SUBTITLE,
      ground: t.ground, ink: t.ink, accent: t.accent, logoUrl: t.logoUrl, copy: t.copy ?? {}, screen: s,
      ...(t.backdrop ? { backdrop: t.backdrop } : {}),
      ...(t.backgroundUrl ? { backgroundUrl: t.backgroundUrl } : {}),
      ...(t.layout ? { layout: t.layout } : {}),
      ...(t.cardOpacity !== undefined ? { cardOpacity: t.cardOpacity } : {}),
      ...(t.logoSize !== undefined ? { logoSize: t.logoSize } : {}),
      ...(t.contact ? { contact: t.contact } : {}),
      ...(t.documents ? { documents: t.documents } : {}),
    };
    win.postMessage(message, location.origin);
  };

  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.source !== frame.current?.contentWindow) return;
      if ((e.data as { type?: string } | null)?.type === PREVIEW_READY) {
        setReady(true);
        post();
      }
    };
    addEventListener("message", on);
    return () => removeEventListener("message", on);
  }, []);

  useEffect(() => {
    if (ready) post();
  }, [ready, theme, screen]);

  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setAvailable(entries[0]?.contentRect.width ?? null));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = available && available < width ? available / width : 1;
  return (
    <Box ref={box} sx={{ width: "100%", height: height * scale, overflow: "hidden", display: "flex", justifyContent: scale < 1 ? "flex-start" : "center" }}>
      <Box
        component="iframe"
        ref={frame}
        title="Portal preview"
        src="/portal.html?preview=1"
        sx={{
          border: 0, borderRadius: 1, bgcolor: "background.paper", boxShadow: 3, display: "block", flexShrink: 0,
          width, height, transform: scale < 1 ? `scale(${scale})` : "none", transformOrigin: "top left",
        }}
      />
    </Box>
  );
}

export const SCREEN_LABELS: Record<PreviewScreen, string> = {
  login: "Sign in",
  register: "Sign up",
  forgot: "Forgot password",
  myplan: "My plan",
  recharge: "Recharge",
  payment: "Payment",
  connected: "Online",
  already: "Already signed in",
  loggedout: "Logged out",
  loggingin: "Logging in",
  legal: "Legal & Terms",
};
